import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../../../../../../../../components/Common/UI/Dialog';
import { cn } from '../../../../../../../../utils/styles';
import Icons from '../../../../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../../../../components/Common/UI/Button';
import BatchDownloadTable from './Table';
import { Input } from '../../../../../../../../components/Common/UI/Input';
import Account from '../../../../../../../../Guidance/components/Pages/BatchDownload/Search/Account';
import {
  batchDocumentsData,
  DocumentTypesEnum,
  EnumBatchDocumentsStatus,
  IBatchDocumentsData,
  ISearchResultData,
} from '../../../../../../../../Guidance/components/Pages/BatchDownload/Data';
import DateFilter from './DateFilters';
import CheckBox from '../../../../../../../../Guidance/components/Common/UI/CheckBox';
import { accountData, IAccount, IAccountData } from './Data';
import Pagination from '../../../../../../../../components/Common/UI/Pagination';

export interface IBatchDownloadState {
  from: string;
  to: string;
  accounts: IAccount[];
  allAccounts: boolean;
  allDocuments: boolean;
  statements: boolean;
  taxes: boolean;
  notices: boolean;
}

const accounts = accountData
  .map((el: IAccountData) => el.accounts)
  .flatMap((subArray: IAccount[]) => subArray)
  .map((el2: IAccount, index: number) => ({ ...el2, id: index.toString(), checked: false }));

const initialState: IBatchDownloadState = {
  from: 'Mar 20 2021',
  to: 'Mar 20 2021',
  accounts,
  allAccounts: false,
  allDocuments: false,
  notices: false,
  statements: false,
  taxes: false,
};

function ModalBatchDownload() {
  const topRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);

  const [searchResult, setSearchResult] = useState<ISearchResultData[]>([]);
  const [_data, setData] = useState<IBatchDocumentsData[]>([]);

  useEffect(() => {
    setData(batchDocumentsData);
  }, []);

  useEffect(() => {
    if (!searchResult.length) setData(batchDocumentsData);
  }, [searchResult]);

  const handleSearchResult = (payload: ISearchResultData[]) => setSearchResult(payload);

  const [selectAllRows, setSelectAllRows] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [state, dispatch] = useReducer(
    (prevState: IBatchDownloadState, action: Partial<IBatchDownloadState>): IBatchDownloadState => ({
      ...prevState,
      ...action,
    }),
    initialState,
  );

  const handleDispatch = (payload: Partial<IBatchDownloadState>) => {
    dispatch(payload);
  };

  const handleCheck = (id: string) => {
    handleDispatch({
      allAccounts: false,
      accounts: state.accounts.map((el: IAccount) => (el.id === id ? { ...el, checked: !el.checked } : el)),
    });
  };

  const handleAllAccounts = (payload: boolean) => {
    handleDispatch({
      allAccounts: payload,
      accounts: state.accounts.map((el: IAccount) => ({ ...el, checked: payload })),
    });
  };

  const handleAllDocuments = (payload: boolean) => {
    handleDispatch({
      allDocuments: payload,
      statements: payload,
      taxes: payload,
      notices: payload,
    });
  };

  const handleOnlyChecked = () => {
    handleDispatch({ accounts: state.accounts.filter((el: IAccount) => el.checked) });
  };

  const handleSearch = () => {
    let documentType = 'Statement';

    if (state.allDocuments || state.statements) {
      documentType = 'Statement';
    } else if (state.notices) {
      documentType = 'Notices';
    } else if (state.taxes) {
      documentType = 'Taxes';
    }

    const resultData: ISearchResultData[] = [];
    const randomStrings = ['JAN 12, 2022', 'MAR 25, 2023', 'DEC 31, 2023', 'FEB 27, 2024'];

    state.accounts
      .filter((el: IAccount) => el.checked)
      .forEach((el: IAccount, accountIndex: number) => {
        for (let i = 0; i < 10; i++) {
          // 10 документов на каждый аккаунт -> 55*10 = 550
          const randomIndex = Math.floor(Math.random() * randomStrings.length);

          resultData.push({
            id: `${accountIndex}-${i}`,
            account: el.number,
            accountId: el.id,
            date: randomStrings[randomIndex],
            documentType,
            checked: false,
            pageCount: '1',
            from: dayjs(state.from).format('MMM DD YYYY'),
            to: dayjs(state.to).format('MMM DD YYYY'),
          } as ISearchResultData);
        }
      });

    setSelectAllRows(false);
    handleSearchResult(resultData);
  };

  const handleSelectAllRows = (payload: boolean) => {
    setSelectAllRows(payload);
    handleSearchResult(searchResult.map(el => ({ ...el, checked: payload })));
  };

  const handleCompressIntoPDF = () => {
    const checked = searchResult.filter(el => el.checked);

    if (!checked.length) {
      return;
    }

    const newDocument: IBatchDocumentsData = {
      id: '2',
      submittedDate: `${dayjs().format('MMM DD, YYYY HH:mm')} PDT`,
      from: checked[0].from || 'MAR 20, 2024',
      to: checked[0].to || 'MAR 20, 2024',
      accounts: checked[0].account,
      documentsType: checked[0].documentType,
      status: EnumBatchDocumentsStatus.pending,
      type: 'COMBINED',
      expiration: `${dayjs().add(1, 'month').format('MMM DD, YYYY HH:mm')} PDT`,
    };

    setData([...batchDocumentsData, newDocument]);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChangePage = (payload: number) => {
    if (payload >= 1) {
      setPage(payload);
    }
  };

  const itemsPerPage = 24;
  const filteredAccounts = useMemo(
    () => state.accounts.filter((el: IAccount) => el.number.includes(searchQuery.length >= 3 ? searchQuery : '')),
    [state.accounts, searchQuery],
  );

  const paginatedAccounts = useMemo(
    () => filteredAccounts.slice((page - 1) * itemsPerPage, page * itemsPerPage),
    [filteredAccounts, page],
  );

  const scrollModalTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Dialog open={modalOpen} onOpenChange={isOpen => setModalOpen(isOpen)}>
      <DialogTrigger className="flex items-center gap-[10px] !bg-primary py-[10px] px-[20px] !text-white">
        <Icons.DownloadIconNew />
        Batch Download Document
      </DialogTrigger>
      <DialogContent className={cn('max-h-[98%] max-w-[1200px]')}>
        <DialogTitle className="mb-[10px] font-normal text-[16px] flex gap-[20px] items-start">
          <div
            className="bg-[#307BB2] p-[5px] rounded-lg w-[40px] h-[35px] flex items-center justify-center"
            ref={topRef}
          >
            <Icons.Newspaper className="[&_path]:fill-white w-[20px] h-[20px]" />
          </div>
          <div>
            <div className="font-bold text-xl leading-[24px]">Batch Download Document</div>
            <div className="opacity-60 max-w-[90%]">
              To filter which accounts you want to combine the statements into Batch Job, please select those account
              number(s), document types and date range
            </div>
          </div>
        </DialogTitle>
        <div className="bg-white p-[20px] border grid gap-[35px]">
          <DateFilter from={state.from} to={state.to} handleDispatch={handleDispatch} />
          <div className="gap-[35px] grid grid-cols-[auto,1fr] items-start">
            <div>
              <div className="flex items-center gap-[20px] md:justify-end md:my-0 my-[20px]">
                <Button className="underline p-0 text-[14px] text-addition1" variant="link" onClick={handleOnlyChecked}>
                  Show Only Selected
                </Button>
                <div className="text-[#898989] text-[14px] flex gap-[5px] items-center">
                  <div className="text-addition6">
                    {state.accounts?.filter((el: IAccount) => el.checked).length || 0}
                  </div>
                  of
                  <div className="text-addition1">{state.accounts?.length || 0}</div>
                  Selected
                </div>
              </div>
              <div className="grid xxl:grid-cols-[275px,auto,1fr] gap-[30px]">
                <div>
                  <div className="flex gap-[20px] mb-[20px] xxl:justify-between">
                    Account Numbers
                    <CheckBox
                      id="selectAll"
                      label="Select All"
                      checked={state.allAccounts}
                      onClick={() => handleAllAccounts(!state.allAccounts)}
                    />
                  </div>
                  <div className="flex xxl:justify-end">
                    <Input
                      iconPosition="left"
                      sizeVariant="md"
                      variant="search"
                      placeholder="Search"
                      className="xxl:w-[200px] xs:w-[300px] w-full"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      icon={<Icons.Search />}
                    />
                  </div>
                </div>
                <div className="bg-black xxl:w-[1px] w-full xxl:h-full h-[1px]" />
                <div
                  className="grid xxl:grid-cols-[repeat(3,auto)] sm:grid-cols-[repeat(2,auto)]
          md:grid-cols-[repeat(3,auto)] lg:gap-x-[60px] md:gap-x-[40px] sm:gap-x-[20px] gap-y-[20px]
          mt-[2px] justify-start items-start"
                >
                  {paginatedAccounts.filter((el: IAccount) =>
                    el.number.includes(searchQuery.length >= 3 ? searchQuery : ''),
                  ).length === 0 ? (
                    <div className="uppercase text-addition1 text-[12px] font-medium opacity-80 cursor-pointer">
                      No items selected
                    </div>
                  ) : (
                    paginatedAccounts
                      .filter((el: IAccount) => el.number.includes(searchQuery.length >= 3 ? searchQuery : ''))
                      .map((el: IAccount) => <Account key={el.id} account={el} handleCheck={handleCheck} />)
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-[30px] flex justify-end mb-[10px]">
            <Pagination
              current={page}
              total={Math.ceil(filteredAccounts.length / itemsPerPage)}
              handlePageChange={handleChangePage}
            />
          </div>
          <div className="grid md:grid-cols-[275px,auto,1fr] md:gap-[30px] gap-[20px]">
            <div className="flex gap-[20px] md:justify-between">
              Documents Types
              <CheckBox
                id="selectAllTypes"
                label="Select All"
                checked={state.allDocuments}
                onClick={() => handleAllDocuments(!state.allDocuments)}
              />
            </div>
            <div className="bg-black w-[1px] h-full md:block hidden" />
            <div
              className="grid grid-cols-[repeat(3,auto)] md:gap-x-[30px] gap-x-[20px] gap-y-[20px]
                justify-start items-start"
            >
              {Object.entries(DocumentTypesEnum).map(([key, value]) => (
                <CheckBox
                  id={key}
                  key={key}
                  label={value}
                  checked={!!state[key as keyof IBatchDownloadState]}
                  onClick={() =>
                    handleDispatch({
                      [key]: !state[key as keyof IBatchDownloadState],
                      allDocuments: false,
                    })
                  }
                />
              ))}
            </div>
          </div>
          <div className="flex sm:justify-center mt-[30px] sm:gap-[40px] gap-[20px] flex-wrap">
            <Button
              className="xs:w-[170px] w-full h-[38px] p-0 !bg-[#965BF3] !text-white uppercase hover:opacity-80"
              onClick={() => {
                handleDispatch(initialState);
                setSearchQuery('');
                handleSearchResult([]);
                handleCompressIntoPDF();
              }}
            >
              Reset
            </Button>
            <Button
              className="xs:w-[170px] w-full h-[38px] p-0 !bg-primary !text-white uppercase hover:opacity-80"
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
        </div>

        <BatchDownloadTable
          searchResult={searchResult}
          searchQuery={searchQuery}
          handleCompressIntoPDF={handleCompressIntoPDF}
          selectAllRows={selectAllRows}
          handleSearchResult={handleSearchResult}
          handleSelectAllRows={handleSelectAllRows}
          onScrollTop={scrollModalTop}
        />
      </DialogContent>
    </Dialog>
  );
}

export default ModalBatchDownload;
